<template>
  <b-overlay :show="formShow" rounded="sm" no-fade class="mt-1">
    <b-card>
      <b-row class="mb-1">
        <b-col
          cols="12"
          md="7"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <b-button
            variant="outline-primary"
            class="btn-icon"
            @click="$router.go(-1)"
          >
            <i class="fa-solid fa-arrow-left"></i>
            <span class="ml-25">Back</span>
          </b-button>
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <table class="table table-sm">
          <thead>
            <tr>
              <th scope="col">
                {{ narrativeId ? "Narrative Edit" : "Narrative Add" }}
              </th>
            </tr>
          </thead>
        </table>
      </b-row>

      <validation-observer ref="narrativeRules">
        <b-form>
          <b-row>
            <b-col cols="6" md="12" sm="12">
              <b-form-group label="Narrative Title" label-for="title">
                <validation-provider
                  name="title"
                  #default="{ errors }"
                  rules="required"
                >
                  <textarea
                    v-model="narrativeValue.title"
                    class="form-control"
                    id="title"
                    name="title"
                    rows="3"
                  ></textarea>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="6" md="12" sm="12">
              <b-form-group label="KPI" label-for="kpi">
                <validation-provider
                  name="kpi"
                  #default="{ errors }"
                  rules="required"
                >
                  <v-select
                    v-model="narrativeValue.kpi_id"
                    label="title"
                    :reduce="(val) => val.id"
                    :options="kpis"
                    :clearable="true"
                  ></v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <hr />

          <b-row>
            <b-col cols="12">
              <b-button
                type="submit"
                @click.prevent="formSubmitted"
                variant="primary"
                class="float-right"
              >
                {{ narrativeId ? "Update Narrative" : "Create Narrative" }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </b-overlay>
</template>

<script>
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";

export default {
  components: {
    vSelect,
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },

  setup() {
    const ACTION_CODE_APP_STORE_MODULE_NAME = "settings";

    if (!store.hasModule(ACTION_CODE_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        ACTION_CODE_APP_STORE_MODULE_NAME,
        settingsStoreModule
      );
    }

    onUnmounted(() => {
      if (store.hasModule(ACTION_CODE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(ACTION_CODE_APP_STORE_MODULE_NAME);
      }
    });
  },

  data() {
    return {
      narrativeValue: {
        title: "",
        kpi_id: "",
      },
      kpis: [],
      formShow: false,
      narrativeId: null,
    };
  },

  mounted() {
    this.initializeForm();
  },

  methods: {
    async initializeForm() {
      try {
        await this.getkpis();

        if (router.currentRoute.params.id) {
          this.narrativeId = parseInt(router.currentRoute.params.id);
          await this.getNarrativeById();
        }
      } catch (error) {
        this.handleError("Failed to initialize form.");
      }
    },

    getkpis() {
      return store.dispatch("settings/getAllKPIs").then((res) => {
        this.kpis = res.data;
      });
    },

    getNarrativeById() {
      return store
        .dispatch("settings/getNarrativeById", { id: this.narrativeId })
        .then((res) => {
          this.narrativeValue = res.data;
        });
    },

    formSubmitted() {
      this.formShow = true;

      this.$refs.narrativeRules.validate().then((success) => {
        if (success) {
          const narrative = this.narrativeId
            ? "settings/updateNarrative"
            : "settings/saveNarrative";

          store
            .dispatch(narrative, this.narrativeValue)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Successful",
                  text: this.narrativeId
                    ? "✔️ Narrative Update Successful"
                    : "✔️ Narrative Added Successfully",
                  icon: "ThumbsUpIcon",
                  variant: "success",
                },
              });

              router.push({ name: "settings-narratives" });
            })
            .catch(() => {
              this.handleError("An error occurred while saving the narrative.");
            })
            .finally(() => {
              this.formShow = false;
            });
        } else {
          this.formShow = false;
        }
      });
    },

    handleError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          text: message,
          icon: "AlertCircleIcon",
          variant: "danger",
        },
      });
      this.formShow = false;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
